import { useIntl } from 'react-intl';
import { useMediaQuery } from 'react-responsive';
import { Badge, breakpoints, Button, Card } from '@openedx/paragon';
import { formatDate } from 'helpers/utils/date';
import { messages } from '../messages';
import { DiscountProductCardProps } from './types';

const COMMERCE_COORDINATOR_BASE_URL =
  process.env.NEXT_PUBLIC_EDX_COMMERCE_COORDINATOR_BASE_URL!;
const PAYMENT_PAGE_REDIRECT_URL = `${COMMERCE_COORDINATOR_BASE_URL}/lms/payment_page_redirect/`;

export const DiscountProductCard = ({
  product,
  isLoading,
  discountCode,
}: DiscountProductCardProps) => {
  const { formatMessage } = useIntl();

  const { small, extraSmall } = breakpoints as Record<
    string,
    { maxWidth: number }
  >;

  const isSmall = useMediaQuery({ maxWidth: small?.maxWidth });
  const isExtraSmall = useMediaQuery({ maxWidth: extraSmall?.maxWidth });
  let discountType = '';
  let discountCurrency = '$';
  let isEnrollment = false;

  if (product.discount.type === 'relative') {
    discountType = '%';
    discountCurrency = '';
    if (product.discount.value === 100) {
      isEnrollment = true;
    }
  }

  const truncatedProductTitle = (title: string) =>
    title.length > 40 ? `${title.slice(0, 40)}...` : title;

  const footer = (
    <>
      <span className="product-price">
        <s>${product.totalPrice}</s>
      </span>{' '}
      <span className="discounted-price">
        {formatMessage(messages['redeem.discount.product.discount.now'])}$
        {product.discountedPrice}
      </span>
    </>
  );

  const redirectParams = new URLSearchParams({
    course_run_key: product.courseRunKey,
    code: discountCode,
    'redeem-discount': 'true',
  });

  return (
    <Card
      orientation={isSmall ? 'vertical' : 'horizontal'}
      isLoading={isLoading}
    >
      <div className="position-relative card-image-container">
        <Card.ImageCap
          src={product.imageUrl}
          srcAlt="Course image"
          className={`card-image ${!product.imageUrl ? 'image-border' : ''}`}
          thumbnail
        />

        <Badge variant="success" className="discount-badge start-0 m-2">
          {`${discountCurrency}${product.discount.value}${discountType} ${formatMessage(messages['redeem.discount.product.off'])}`}
        </Badge>

        <div className="discount-valid-until">
          <p>
            {formatMessage(messages['redeem.discount.product.discount.end'])}
            {product.discountValidUntil &&
              formatDate(product.discountValidUntil)}
          </p>
        </div>
      </div>
      <Card.Body>
        <Card.Header
          title={truncatedProductTitle(product.title)}
          subtitle={product.organization || ' '} // eslint-disable-line
          size="sm"
        />

        <Card.Section>
          {formatMessage(messages['redeem.discount.product.course.start'])}{' '}
          {product.courseStartDate && formatDate(product.courseStartDate)}
        </Card.Section>

        <Card.Footer
          orientation={isExtraSmall ? 'horizontal' : 'vertical'}
          textElement={footer}
        >
          <Button
            as="a"
            href={`${PAYMENT_PAGE_REDIRECT_URL}?${redirectParams.toString()}`}
          >
            {formatMessage(
              isEnrollment
                ? messages['redeem.discount.product.discount.enrollment']
                : messages['redeem.discount.product.discount.checkout'],
            )}
          </Button>
        </Card.Footer>
      </Card.Body>
    </Card>
  );
};
